/**
 * The curated, self-hosted font set (FR-I11).
 *
 * **Self-hosted is the requirement, not a preference.** A public page that
 * pulls a font from `fonts.googleapis.com` tells a third party the IP address
 * of every visitor to a firm's website, and ADR-0013 §2 says no client-side
 * framework runs on a public page — a font CDN is the same class of thing
 * arriving through a different door. So the files ship in the image: they come
 * from `@fontsource/*` (SIL Open Font Licence, the same files Google serves)
 * and `scripts/copy-fonts.mjs` copies the Latin subset into
 * `dist/site/fonts/` at build time.
 *
 * Two weights per family, 400 and 600. A third weight is a third download on a
 * phone for a difference nobody has asked for.
 */

export interface CuratedFont {
  /** The value stored in `theme.font`. Stable — it is in a firm's database row. */
  key: string
  /** What the firm sees in the theme panel (4D). */
  label: string
  /** The `font-family` name declared by `@font-face` and used in the stack. */
  family: string
  /** The npm package the files come from. */
  pkg: string
  /** The `@fontsource` file stem, `<stem>-<weight>-normal.woff2`. */
  stem: string
  /** What renders while the font loads, and forever if the file 404s. */
  fallback: string
  /** One line, for the panel. */
  trait: string
  /**
   * The weights this family ships, when it is not `WEIGHTS`. A display face
   * such as Abril Fatface is cut in one weight only, and declaring a 600 it
   * does not have would be a 404 on every page that used it.
   */
  weights?: readonly number[]
  /**
   * C21: a face cut in **one** weight is declared across the whole `400 600`
   * range, so a heading a theme sets at 600 draws the real face rather than a
   * bold the browser synthesises by smearing the 400. DM Serif Display and
   * Bebas Neue (400 only, I-3). Abril Fatface predates it and is left as it
   * was, so no page drawn before C21 changes.
   */
  weightRange?: boolean
}

export const FONTS: CuratedFont[] = [
  {
    key: 'inter',
    label: 'Inter',
    family: 'Inter',
    pkg: '@fontsource/inter',
    stem: 'inter-latin',
    fallback: "system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif",
    trait: 'Neutral sans — reads as a document, not a brand',
  },
  {
    key: 'source-sans',
    label: 'Source Sans 3',
    family: 'Source Sans 3',
    pkg: '@fontsource/source-sans-3',
    stem: 'source-sans-3-latin',
    fallback: "system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif",
    trait: 'Humanist sans — warmer, still plain',
  },
  {
    key: 'source-serif',
    label: 'Source Serif 4',
    family: 'Source Serif 4',
    pkg: '@fontsource/source-serif-4',
    stem: 'source-serif-4-latin',
    fallback: "Georgia, 'Times New Roman', serif",
    trait: 'Transitional serif — the register of a printed opinion',
  },
  {
    key: 'lora',
    label: 'Lora',
    family: 'Lora',
    pkg: '@fontsource/lora',
    stem: 'lora-latin',
    fallback: "Georgia, 'Times New Roman', serif",
    trait: 'Contemporary serif — a softer formality',
  },
  {
    key: 'manrope',
    label: 'Manrope',
    family: 'Manrope',
    pkg: '@fontsource/manrope',
    stem: 'manrope-latin',
    fallback: "system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif",
    trait: 'Geometric sans — open and current',
  },
  // C17 (ADR-0036 §6): two more body faces, on the owner's word — which
  // re-opens what spec §9-59 closed. Space Grotesk is also a heading face below
  // (Costline's); the two entries name the same files, so a page that uses it
  // twice declares the same `@font-face` twice and fetches it once.
  {
    key: 'dm-sans',
    label: 'DM Sans',
    family: 'DM Sans',
    pkg: '@fontsource/dm-sans',
    stem: 'dm-sans-latin',
    fallback: "system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif",
    trait: 'Low-contrast geometric sans — clean and young',
  },
  {
    key: 'space-grotesk',
    label: 'Space Grotesk',
    family: 'Space Grotesk',
    pkg: '@fontsource/space-grotesk',
    stem: 'space-grotesk-latin',
    fallback: "system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif",
    trait: 'Grotesque with monospace quirks — technical and figure-led',
  },
  // C21's four body faces (ADR-0043 §6, waves.md Wave 21 I-3) — the ones
  // Wave 21's four themes offer in `bodyChoices`, and no face no theme offers.
  // Each is cut at 400 and 600 in `@fontsource` 5.3.0.
  {
    key: 'plus-jakarta-sans',
    label: 'Plus Jakarta Sans',
    family: 'Plus Jakarta Sans',
    pkg: '@fontsource/plus-jakarta-sans',
    stem: 'plus-jakarta-sans-latin',
    fallback: "system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif",
    trait: 'Modern geometric sans — crisp and confident',
  },
  {
    key: 'work-sans',
    label: 'Work Sans',
    family: 'Work Sans',
    pkg: '@fontsource/work-sans',
    stem: 'work-sans-latin',
    fallback: "system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif",
    trait: 'Grotesque drawn for the screen — plain and businesslike',
  },
  {
    key: 'figtree',
    label: 'Figtree',
    family: 'Figtree',
    pkg: '@fontsource/figtree',
    stem: 'figtree-latin',
    fallback: "system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif",
    trait: 'Friendly geometric sans — clear at every size',
  },
  {
    key: 'outfit',
    label: 'Outfit',
    family: 'Outfit',
    pkg: '@fontsource/outfit',
    stem: 'outfit-latin',
    fallback: "system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif",
    trait: 'Rounded geometric sans — young and product-like',
  },
  // Wave 22's two body faces (ADR-0043 §6, waves.md Wave 22 I-4) — the ones
  // Haven, Stillwater, Campus and Kindred offer in `bodyChoices` that were not
  // shipped already. Each is cut at 400 and 600 in `@fontsource` 5.3.0.
  {
    key: 'nunito-sans',
    label: 'Nunito Sans',
    family: 'Nunito Sans',
    pkg: '@fontsource/nunito-sans',
    stem: 'nunito-sans-latin',
    fallback: "system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif",
    trait: 'Soft humanist sans — gentle and very readable',
  },
  {
    key: 'karla',
    label: 'Karla',
    family: 'Karla',
    pkg: '@fontsource/karla',
    stem: 'karla-latin',
    fallback: "system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif",
    trait: 'Quiet grotesque with a little character — calm and unfussy',
  },
]

export const WEIGHTS = [400, 600] as const

/**
 * **Heading faces a template owns** (ADR-0020 §5) — never a firm's `theme.font`,
 * never in the service's vocabulary, never offered in the theme panel. A
 * template names one in `Template.display` and its headings are set in it
 * whatever body face the firm picks. Self-hosted for the same reason as the
 * set above, and copied by the same script.
 */
export const DISPLAY_FONTS: CuratedFont[] = [
  {
    key: 'playfair-display',
    label: 'Playfair Display',
    family: 'Playfair Display',
    pkg: '@fontsource/playfair-display',
    stem: 'playfair-display-latin',
    fallback: "Georgia, 'Times New Roman', serif",
    trait: 'High-contrast transitional serif',
  },
  {
    key: 'abril-fatface',
    label: 'Abril Fatface',
    family: 'Abril Fatface',
    pkg: '@fontsource/abril-fatface',
    stem: 'abril-fatface-latin',
    fallback: "Georgia, 'Times New Roman', serif",
    trait: 'Didone display — editorial and heavy',
    weights: [400],
  },
  // Wave 10's five (T-4.46, ADR-0026 §3) — landed once, before any template
  // names one, so each theme session chooses from a set rather than adding to
  // it. Every one of them is cut at 400 and 600 in `@fontsource` 5.3.0.
  {
    key: 'eb-garamond',
    label: 'EB Garamond',
    family: 'EB Garamond',
    pkg: '@fontsource/eb-garamond',
    stem: 'eb-garamond-latin',
    fallback: "Garamond, Georgia, 'Times New Roman', serif",
    trait: 'Old-style serif — the book face of a law report',
  },
  {
    key: 'libre-baskerville',
    label: 'Libre Baskerville',
    family: 'Libre Baskerville',
    pkg: '@fontsource/libre-baskerville',
    stem: 'libre-baskerville-latin',
    fallback: "Baskerville, Georgia, 'Times New Roman', serif",
    trait: 'Transitional serif, wide for the screen — sober and established',
  },
  {
    key: 'space-grotesk',
    label: 'Space Grotesk',
    family: 'Space Grotesk',
    pkg: '@fontsource/space-grotesk',
    stem: 'space-grotesk-latin',
    fallback: "system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif",
    trait: 'Grotesque with monospace quirks — technical and figure-led',
  },
  {
    key: 'poppins',
    label: 'Poppins',
    family: 'Poppins',
    pkg: '@fontsource/poppins',
    stem: 'poppins-latin',
    fallback: "system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif",
    trait: 'Geometric sans — round, friendly and confident',
  },
  {
    key: 'inter-tight',
    label: 'Inter Tight',
    family: 'Inter Tight',
    pkg: '@fontsource/inter-tight',
    stem: 'inter-tight-latin',
    fallback: "system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif",
    trait: 'Neo-grotesque, tightly spaced — Inter set for headlines',
  },
  // C17's three (ADR-0036 §6) — the faces a firm may choose **where its theme
  // offers a choice** (`Template.displayChoices`, `theme.display`); Unbounded
  // is Encore's default. Each is cut at 400 and 600 in `@fontsource` 5.x, and
  // nothing heavier is declared (ADR-0026 §7).
  {
    key: 'unbounded',
    label: 'Unbounded',
    family: 'Unbounded',
    pkg: '@fontsource/unbounded',
    stem: 'unbounded-latin',
    fallback: "system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif",
    trait: 'Wide, rounded display sans — loud at any size',
  },
  {
    key: 'syne',
    label: 'Syne',
    family: 'Syne',
    pkg: '@fontsource/syne',
    stem: 'syne-latin',
    fallback: "system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif",
    trait: 'Art-school grotesque — wide and a little strange',
  },
  {
    key: 'bricolage-grotesque',
    label: 'Bricolage Grotesque',
    family: 'Bricolage Grotesque',
    pkg: '@fontsource/bricolage-grotesque',
    stem: 'bricolage-grotesque-latin',
    fallback: "system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif",
    trait: 'Condensed-leaning grotesque with ink traps — poster-like',
  },
  // C21's six (ADR-0043 §6, waves.md Wave 21 I-3) — the heading faces Wave
  // 21's four themes offer in `displayChoices`. At 400 and 600, except the two
  // `@fontsource` cuts in one weight only, which are declared across the range
  // (`weightRange`). Outfit is a body face too, from the same files, as Space
  // Grotesk is — and `render.tsx` declares a family once when it is both.
  {
    key: 'fraunces',
    label: 'Fraunces',
    family: 'Fraunces',
    pkg: '@fontsource/fraunces',
    stem: 'fraunces-latin',
    fallback: "Georgia, 'Times New Roman', serif",
    trait: 'Soft old-style serif — warm, a little wry',
  },
  {
    key: 'dm-serif-display',
    label: 'DM Serif Display',
    family: 'DM Serif Display',
    pkg: '@fontsource/dm-serif-display',
    stem: 'dm-serif-display-latin',
    fallback: "Georgia, 'Times New Roman', serif",
    trait: 'High-contrast display serif — elegant at large sizes',
    weights: [400],
    weightRange: true,
  },
  {
    key: 'archivo',
    label: 'Archivo',
    family: 'Archivo',
    pkg: '@fontsource/archivo',
    stem: 'archivo-latin',
    fallback: "system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif",
    trait: 'Grotesque built for headlines — sturdy and corporate',
  },
  {
    key: 'sora',
    label: 'Sora',
    family: 'Sora',
    pkg: '@fontsource/sora',
    stem: 'sora-latin',
    fallback: "system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif",
    trait: 'Wide geometric sans — technical and open',
  },
  {
    key: 'bebas-neue',
    label: 'Bebas Neue',
    family: 'Bebas Neue',
    pkg: '@fontsource/bebas-neue',
    stem: 'bebas-neue-latin',
    fallback: "Impact, 'Arial Narrow', system-ui, sans-serif",
    trait: 'Condensed all-caps display — a poster headline',
    weights: [400],
    weightRange: true,
  },
  {
    key: 'outfit',
    label: 'Outfit',
    family: 'Outfit',
    pkg: '@fontsource/outfit',
    stem: 'outfit-latin',
    fallback: "system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif",
    trait: 'Rounded geometric sans — young and product-like',
  },
  // Wave 22's two (ADR-0043 §6, waves.md Wave 22 I-4) — the heading faces
  // Stillwater offers that were not shipped already. Cormorant Garamond at 400
  // and 600; Marcellus is cut in one weight and declared across the range
  // (`weightRange`), as Wave 21 did for DM Serif Display and Bebas Neue.
  {
    key: 'cormorant-garamond',
    label: 'Cormorant Garamond',
    family: 'Cormorant Garamond',
    pkg: '@fontsource/cormorant-garamond',
    stem: 'cormorant-garamond-latin',
    fallback: "Georgia, 'Times New Roman', serif",
    trait: 'Light, tall Garamond — airy and composed',
  },
  {
    key: 'marcellus',
    label: 'Marcellus',
    family: 'Marcellus',
    pkg: '@fontsource/marcellus',
    stem: 'marcellus-latin',
    fallback: "Georgia, 'Times New Roman', serif",
    trait: 'Flared inscriptional capitals — serene and classical',
    weights: [400],
    weightRange: true,
  },
  // C23 (ADR-0045 §2): Summit's heading face. It is already a body face (C23,
  // above); as with Outfit and Space Grotesk, the two entries name the same
  // files and `render.tsx` declares the family once when it is both.
  {
    key: 'plus-jakarta-sans',
    label: 'Plus Jakarta Sans',
    family: 'Plus Jakarta Sans',
    pkg: '@fontsource/plus-jakarta-sans',
    stem: 'plus-jakarta-sans-latin',
    fallback: "system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif",
    trait: 'Geometric sans with open counters — bright and assured',
  },
  // Wave 23's one (ADR-0043 §6, waves.md Wave 23 I-5) — Blade's heading face,
  // the only face Wave 23's five offer that was not shipped already. Cut at
  // 400 and 600 in `@fontsource` 5.3.0.
  {
    key: 'oswald',
    label: 'Oswald',
    family: 'Oswald',
    pkg: '@fontsource/oswald',
    stem: 'oswald-latin',
    fallback: "Impact, 'Arial Narrow', system-ui, sans-serif",
    trait: 'Condensed gothic — tall, sharp and signwritten',
  },
]

/**
 * **The heading faces a firm may choose** (ADR-0036 §6) — the service's
 * `DISPLAY_CHOICES`, in its order. A template offers a subset of these in
 * `displayChoices`; `tests/site-vocabulary.test.ts` holds the two lists together.
 *
 * C21 (ADR-0043 §6, I-3) makes it the union of every theme's `displayChoices`:
 * C17's three, Wave 21's six new faces, then `playfair-display` and
 * `inter-tight` — shipped heading faces already (Chambers, Modern) that become
 * *choosable* because Meridian and Keystone offer them. It stays one global
 * validation list; which of these a firm is *shown* is its theme's. Wave 22
 * (I-4) appends `cormorant-garamond` and `marcellus`, in that order. Wave 23
 * (I-5) appends `oswald`, then `space-grotesk` — a shipped heading face
 * (Costline's) that becomes choosable because Cipher offers it.
 */
export const DISPLAY_CHOICES = [
  'unbounded',
  'syne',
  'bricolage-grotesque',
  'fraunces',
  'dm-serif-display',
  'archivo',
  'sora',
  'bebas-neue',
  'outfit',
  'playfair-display',
  'inter-tight',
  // Wave 22 (I-4).
  'cormorant-garamond',
  'marcellus',
  // Wave 23 (I-5).
  'oswald',
  'space-grotesk',
] as const

const DISPLAY_BY_KEY = new Map(DISPLAY_FONTS.map((f) => [f.key, f]))

/** A template's heading face, or `null` for a template that sets none. */
export function displayFont(key: string | null | undefined): CuratedFont | null {
  return key ? (DISPLAY_BY_KEY.get(key) ?? null) : null
}

/** The weights one family actually ships. */
export function weightsOf(font: CuratedFont): readonly number[] {
  return font.weights ?? WEIGHTS
}

const BY_KEY = new Map(FONTS.map((f) => [f.key, f]))

/**
 * A theme's font, or the template's default when the stored key names nothing
 * we ship. **Never a third-party fetch**: an unknown key falls back to a family
 * whose files are in the image.
 */
export function resolveFont(key: string | null | undefined, fallbackKey: string): CuratedFont {
  const wanted = key ? BY_KEY.get(key) : undefined
  if (wanted) return wanted
  const fallback = BY_KEY.get(fallbackKey)
  if (fallback) return fallback
  // Both unknown: the first curated family, which is always present.
  return FONTS[0] as CuratedFont
}

/** The public URL of one weight's file. Served by `server/static.ts`. */
export function fontUrl(font: CuratedFont, weight: number): string {
  return `/site-assets/fonts/${font.stem}-${weight}-normal.woff2`
}

/**
 * The `@font-face` rules for one family, inlined into the page's `<style>`.
 *
 * `font-display: swap` on purpose: the text must be readable before the font
 * arrives. On a phone on a bad connection, a blocked first paint is the whole
 * mobile score and the whole experience.
 */
export function fontFaceCss(font: CuratedFont): string {
  const weights = weightsOf(font)
  return weights.map(
    (weight) => `@font-face{font-family:'${font.family}';font-style:normal;` +
      `font-weight:${font.weightRange && weights.length === 1 ? '400 600' : weight};` +
      `font-display:swap;src:url('${fontUrl(font, weight)}') format('woff2');}`,
  ).join('')
}

/** The stack that actually lands in `font-family`. */
export function fontStack(font: CuratedFont): string {
  return `'${font.family}', ${font.fallback}`
}
